import { ContactControllerV2 } from './../ui-contact-controller-v2.service';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormControl,
} from '@angular/forms';
import {
  Component,
  Input,
  OnInit,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ComponentInOutAnimation } from '@fullyops/shared/util/component-base-animation';
import {
  FieldType,
  PermissionType,
} from '@fullyops/legacy/ui/ui-shared/utils/crm-types';
import { CompanyFormService } from './company-form.service';
import { Company, User, Contact } from '@fullyops/legacy/data';
import { FormComponent } from '@fullyops/legacy/ui/ui-shared/utils/form.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { UiAuthService } from '@fullyops/legacy/ui';
import { MatDialog } from '@angular/material/dialog';
import { TemplateFormFields } from '@fullyops/legacy/ui/ui-shared/form-template/form-template.component';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ContactRequest,
  ContactResponse,
} from '@fullyops/legacy/data/api/types/Contact';
import { CompanyControllerV2 } from '../ui-company-controller-v2.service';
import { Country } from '@fullyops/legacy/data/models/v0/Country';
import { DialogService } from '@fullyops/legacy/ui/ui-shared/dialog/dialog.service';
import { ApiTenantConfigurationService } from '@fullyops/data/tenant-configuration/api-adapter';
import { noWhitespaceValidator } from '@fullyops/legacy/data/validators/noWhitespaceValidator';
import { ToastService } from '@fullyops/shared/services/toast/toast.service';
import { KebabMenuItem } from '@fullyops/shared/components/kebab-menu/kebab-menu.component';
import { DeleteDialogComponent } from '@fullyops/shared/components/delete-dialog/dialog.component';
import { HistoryItem } from '@fullyops/data/history/history';
import { CompanyInput } from '@fullyops/data/companies/company';
import { CompanyFormComponent as NewCompanyFormComponent } from '@fullyops/features/companies/containers/company-form/company-form.component';
import { Location, LocationInput } from '@fullyops/data/locations/location';
import { LocationService } from '@fullyops/domain/locations/service';
import { LocationListComponent } from '@fullyops/features/locations/containers/location-list/location-list.component';

export type EditContactProps = {
  contact: ContactResponse;
  index: number;
  contactForm: UntypedFormGroup;
};

export type DeleteContactProps = { index: number; contactId: string };

@Component({
  selector: 'crm-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
  providers: [CompanyFormService],
  animations: [ComponentInOutAnimation.AnimTrigger],
  standalone: false,
})
export class CompanyFormComponent
  extends FormComponent<Company, CompanyInput, CompanyFormService>
  implements OnInit, OnDestroy
{
  constructor(
    public companyFormService: CompanyFormService,
    public authService: UiAuthService,
    public toastService: ToastService,
    public dialog: MatDialog,
    private router: Router,
    private contactControllerV2: ContactControllerV2,
    public changeDetectorRef: ChangeDetectorRef,
    protected companyControllerV2: CompanyControllerV2,
    protected route: ActivatedRoute,
    private dialogService: DialogService,
    private tenantService: ApiTenantConfigurationService,
    private locationService: LocationService,
  ) {
    super(toastService, authService);

    this.formService = companyFormService;
    this.formEditPermissions = [PermissionType.CAN_EDIT_COMPANIES];

    this.draft$ = new BehaviorSubject<CompanyInput>({
      name: '',
      externalId: '',
      type: 'CLIENT',
      nif: '',
      assigneeIds: [],
      countryISOCode: null,
      email: '',
      mobileNumber: '',
      website: '',
      notes: '',
    });
  }

  @Input() history$: Observable<HistoryItem[]>;
  @Input() users$: BehaviorSubject<User[]>;
  @Output() openContactEvent = new EventEmitter<string>();
  @Output() addEquipmentEvent = new EventEmitter();
  @Output() removeEquipmentEvent = new EventEmitter<string>();
  @Output() editEquipmentEvent = new EventEmitter<string>();

  @ViewChild(NewCompanyFormComponent)
  private formComponent: NewCompanyFormComponent;

  setError(fieldName: 'email' | 'nif', message: string): void {
    this.formComponent.setError(fieldName, message);
  }

  protected kebabItems: KebabMenuItem[] = [
    { label: 'delete', highlight: 'warn', action: () => this.onDelete() },
  ];

  protected locations$: Observable<Location[]>;

  timeHours = new BehaviorSubject<any[]>([]);
  assignees$ = new BehaviorSubject<[]>([]);
  private subscriptions = new Subscription();
  allContacts$ = new BehaviorSubject<ContactResponse[]>([]);
  companyTypes$ = new BehaviorSubject<{ id: any; name: string }[]>([]);
  countries$ = new BehaviorSubject<Country[]>([]);

  newContactForm = new UntypedFormGroup({
    name: new UntypedFormControl('', [
      Validators.required,
      noWhitespaceValidator,
    ]),
    jobPosition: new UntypedFormControl(''),
    address: new UntypedFormControl(''),
    email: new UntypedFormControl('', [Validators.email]),
    phone: new UntypedFormControl(''),
    notes: new UntypedFormControl(''),
  });
  companyEmailRequired = true;
  bothTypeAllowed = false;
  newContactFields: TemplateFormFields<any> = [
    {
      name: 'name',
      type: 'text',
      label: 'name',
      size: 6,
    },
    {
      name: 'jobPosition',
      type: 'text',
      label: 'jobPosition',
      size: 6,
    },
    {
      name: 'address',
      type: 'text',
      label: 'address',
      size: 12,
    },
    {
      name: 'email',
      type: 'text',
      label: 'email',
      size: 12,
    },
    {
      name: 'phone',
      type: 'text',
      label: 'phone',
      size: 12,
    },
    {
      name: 'notes',
      type: 'textarea',
      label: 'notes',
      size: 12,
    },
  ];

  canViewClientEquipmentPermission = [
    PermissionType.CAN_ACCESS_CLIENT_EQUIPMENTS,
  ];

  faUser = faUser;

  canViewClientEquipment$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  disabled = false;

  selectedTabIndex = new FormControl(0);

  ngOnInit() {
    this.disabled = !this.authService.hasPermissionNoAsync([
      PermissionType.CAN_EDIT_COMPANIES,
    ]);
    this.subscriptions.add(
      this.users$
        .pipe(
          map((users) =>
            users.map((user: User) => ({
              id: user.id,
              name: `${user.firstName} ${user.lastName}`,
            })),
          ),
        )
        .subscribe(this.assignees$),
    );
    this.subscriptions.add(
      this.tenantService
        .getConfigurations({
          groupLabel: 'COMPANIES_CONTACTS',
        })
        .subscribe((r) => {
          this.companyEmailRequired =
            r.results.find((c) => c.label === 'COMPANY_EMAIL_REQUIRED')
              ?.value === 'true';
          this.bothTypeAllowed =
            r.results.find((c) => c.label === 'ALLOW_COMPANY_TYPE_BOTH')
              ?.value === 'true';
          this.initForm();
          this.initHours();
          this.initFields();
          this.loadAllContacts();
          this.setCanViewClientEquipment();
          this.getTypes();
          this.getCountries();
        }),
    );
    this.locationService.refreshByCompanyId(this.entity.id);
    this.locations$ = this.locationService.locationsByCompanyId$(
      this.entity.id,
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.locationService.clearByCompanyId(this.entity.id);
  }

  getInitialUser() {
    const userRoles = this.authService.currentUser.roles.map((r) => r.name);
    if (userRoles.includes('SALESMAN')) {
      return [this.authService.currentUser.id];
    }
    return [];
  }

  getCountries() {
    this.route.data.subscribe((data) => {
      this.countries$.next(data?.countries.filter((c: Country) => c.active));
    });
  }

  getTypes() {
    this.companyControllerV2.getCompaniesTypes().subscribe((types) => {
      this.companyTypes$.next(
        types.map((type) => {
          return { id: type, name: type };
        }),
      );
    });
  }

  setCanViewClientEquipment() {
    return this.authService
      .hasPermission(this.canViewClientEquipmentPermission)
      .subscribe((res) => this.canViewClientEquipment$.next(res));
  }

  loadAllContacts() {
    if (this.entity) {
      this.contactControllerV2
        .getAll({ queryParams: { companyId: this.entity.id } })
        .subscribe((res) => {
          this.allContacts$.next(res.results);
        });
    }
  }

  parseContact(contact: Contact) {
    return {
      id: contact.id,
      columns: {
        name: { label: 'name', type: FieldType.User, value: contact.name },
        email: { label: 'email', type: FieldType.Text, value: contact.email },
        mobileNumber: {
          label: 'phone',
          type: FieldType.Text,
          value: contact.mobileNumber,
        },
        notes: {
          label: 'notes',
          type: FieldType.Text,
          value: contact.notes,
        },
      },
    };
  }

  initFields() {
    this.formFields = [
      { name: 'name', type: 'text', label: 'name', size: 8 },
      { name: 'externalId', type: 'text', label: 'externalId', size: 4 },
      {
        name: 'type',
        type: 'select',
        label: 'type',
        size: 3,
        items$: this.companyTypes$,
        nullable: false,
        translate: true,
      },
      { name: 'nif', type: 'text', label: 'nif', size: 3 },
      {
        name: 'assigneeIds',
        type: 'multiple-select',
        label: 'assignees',
        size: 6,
        items$: this.assignees$,
      },
      {
        name: 'countryISOCode',
        type: 'select',
        label: 'country',
        items$: this.countries$,
        size: 12,
      },
      { name: 'email', type: 'text', label: 'email', size: 6 },
      { name: 'phone', type: 'text', label: 'phone', size: 6 },
      { name: 'website', type: 'text', label: 'website', size: 12 },
      { name: 'notes', type: 'textarea', label: 'notes', size: 12 },
    ];
  }

  initHours() {
    this.timeHours.next(
      new Array(96).fill('', 0, 96).map((_, index) => {
        const value = Math.floor(index / 4);
        if (index % 4 === 0) {
          return { name: `${value}:00`, id: value * 60 };
        } else {
          return {
            name: `${value}:${(index % 4) * 15}`,
            id: value * 60 + (index % 4) * 15,
          };
        }
      }),
    );
  }

  onAddEquipment() {
    this.addEquipmentEvent.emit();
  }

  onRemoveEquipment(id: string) {
    this.removeEquipmentEvent.emit(id);
  }

  onEditEquipment(id: string) {
    this.editEquipmentEvent.emit(id);
  }

  @ViewChild(LocationListComponent, { static: false })
  private locationList: LocationListComponent;

  onCreateLocation(location: LocationInput) {
    this.subscriptions.add(
      this.locationService
        .create({ ...location, companyId: this.entity.id })
        .subscribe(() => this.locationList.closeForm()),
    );
  }

  onEditLocation(location: { id: string; input: LocationInput }) {
    this.subscriptions.add(
      this.locationService
        .update(location.id, location.input)
        .subscribe(() => this.locationList.closeForm()),
    );
  }

  onDeleteLocation(id: string) {
    this.subscriptions.add(
      this.locationService
        .delete(id)
        .subscribe(() => this.locationList.closeForm()),
    );
  }

  onOpenContact(id: string) {
    this.openContactEvent.emit(id);
  }

  onSave(input: CompanyInput) {
    this.subscriptions.add(
      this.companyFormService.form$.subscribe((companyForm) => {
        companyForm.markAllAsTouched();

        if (companyForm.valid) {
          this.saveEvent.emit(input);
        } else {
          this.toastService.showInvalidFieldsFormErrorMessage();
        }
      }),
    );
  }

  createContact() {
    this.newContactForm.controls['email'].markAsDirty();
    this.newContactForm.controls['email'].markAsTouched();
    if (this.newContactForm.valid) {
      const newContact: ContactRequest = {
        address: this.newContactForm.controls['address'].value,
        email: this.newContactForm.controls['email'].value,
        mobileNumber: this.newContactForm.controls['phone'].value,
        name: this.newContactForm.controls['name'].value,
        companyId: this.entity.id,
        notes: this.newContactForm.controls['notes'].value,
        jobPosition: this.newContactForm.controls['jobPosition'].value,
      };
      this.contactControllerV2.create({ newContact }).subscribe((res) => {
        this.allContacts$.next([...this.allContacts$.value, res]);
        this.newContactForm.reset();
      });
    } else {
      this.toastService.showInvalidFieldsFormErrorMessage();
    }
  }

  redirectToContactPage(contactId: string) {
    return this.authService
      .hasPermission([PermissionType.CAN_EDIT_CONTACTS])
      .subscribe((res) => {
        if (res) {
          return this.router.navigate([`/contacts/${contactId}/edit`]);
        }
        return this.router.navigate([`/contacts/${contactId}`]);
      });
  }

  generateContactForm(contact: Contact) {
    const contactForm = new UntypedFormGroup({
      name: new UntypedFormControl(contact.name, [
        Validators.required,
        noWhitespaceValidator,
      ]),
      address: new UntypedFormControl(contact.address),
      email: new UntypedFormControl(contact.email, [Validators.email]),
      phone: new UntypedFormControl(contact.mobileNumber),
      notes: new UntypedFormControl(contact.notes),
    });

    contactForm.disable();

    return contactForm;
  }

  resetContactForm(props: { contactForm: UntypedFormGroup; index: number }) {
    const { contactForm, index } = props;
    const contact = this.allContacts$.value[index];
    contactForm.reset({
      name: contact.name,
      address: contact.address,
      email: contact.email,
      mobileNumber: contact.mobileNumber,
      notes: contact.notes,
    });
  }

  editContact(props: EditContactProps) {
    const { contact, index, contactForm } = props;
    if (contactForm.valid) {
      const newContact: ContactRequest = {
        address: contact.address,
        companyId: contact.company.id,
        email: contact.email,
        mobileNumber: contact.mobileNumber,
        name: contact.name,
        notes: contact.notes,
        jobPosition: contact.jobPosition,
      };
      this.subscriptions.add(
        this.contactControllerV2
          .updateById({ contactId: contact.id, contact: newContact })
          .subscribe(
            (contactResponse) => {
              const oldContacts = [...this.allContacts$.value];
              oldContacts[index] = contactResponse;
              this.allContacts$.next(oldContacts);
            },
            () => {
              this.resetContactForm({ contactForm, index });
            },
          ),
      );
    } else {
      this.toastService.showInvalidFieldsFormErrorMessage();
    }
  }

  deleteContact(props: DeleteContactProps) {
    const dialogRef = this.dialogService.openDialogBeforeDelete();

    dialogRef.afterClosed().subscribe((saveOutput) => {
      if (saveOutput) {
        const { contactId, index } = props;
        this.contactControllerV2.deleteById({ contactId }).subscribe((res) => {
          const newContacts = [...this.allContacts$.value];
          newContacts.splice(index, 1);
          this.allContacts$.next(newContacts);
        });
      }
    });
  }

  onTabChange(i: number) {
    this.selectedTabIndex.setValue(i);
  }

  onDelete() {
    const sub = this.dialog
      .open(DeleteDialogComponent)
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          super.onDelete();
        }
      });
    this.subscriptions.add(sub);
  }
}
